import { putShareTrackList } from "@api/tracklists";
import type { Tracklist } from "@api/tracklists/types";
import VoxtoxLogo from "@assets/icons/voxtox.svg";
import Dialog from "@components/Dialog";
import { toast } from "@components/Toaster";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import { ENV } from "@src/env";
import mixpanel from "mixpanel-browser";
import {
	FaBrandsFacebookF,
	FaBrandsLinkedin,
	FaBrandsTwitter,
	FaRegularClipboard,
	FaRegularEnvelope,
	FaSolidCode,
} from "solid-icons/fa";
import { type ParentComponent, Show } from "solid-js";
import { ButtonsGrid, ShareButton } from "./styled";

const ShareDialog: ParentComponent<{
	tracklist?: Tracklist;
	shareOnVoxtox?: boolean;
	trackIndex?: number;
	onOpenChange?: (open: boolean) => void;
}> = (props) => {
	const { t } = useI18n();

	const getShareLink = (site?: string) => {
		if (!props.tracklist) return;
		if (site && import.meta.env.VITE_MIXPANEL)
			mixpanel.track(`TrackList Share - ${site} Share`, {
				tracklist_uuid: props.tracklist.uuid,
			});
		return encodeURI(
			`${window.location.origin}/t/${props.tracklist.uuid}/${props.trackIndex === undefined ? 0 : props.trackIndex}`,
		);
	};

	const handleShareVoxtox = () => {
		if (!props.tracklist) return;
		const p = putShareTrackList(props.tracklist.uuid);
		toast.promise(p, {
			loading: t("app.programme.sharing.loading"),
			success: () => t("app.programme.sharing.success"),
			error: () => t("app.programme.sharing.error"),
		});
		props.onOpenChange?.(false);
	};

	const handleShareCode = () => {
		if (!props.tracklist) return;
		if (ENV.VITE_MIXPANEL) mixpanel.track("TrackList Share - Code Share");
		toast.success(t("global.copied"));
		navigator.clipboard.writeText(
			`<iframe src='${window.location.origin}/embed/tracklist/${props.tracklist.uuid}' width="600" height="480px" style="border:none;border-radius:15px;"></iframe>`,
		);
	};
	const handleShare = () => {
		toast.success(t("global.copied"));
		navigator.clipboard.writeText(getShareLink("Link") ?? "");
	};

	return (
		<Dialog
			openTrigger={props.children}
			open={!!props.tracklist}
			onOpenChange={props.onOpenChange}
			title={`${t("app.programme.share")} ${props.tracklist?.name}`}
		>
			<ButtonsGrid>
				<Show when={props.shareOnVoxtox}>
					<ShareButton onClick={handleShareVoxtox}>
						<img src={VoxtoxLogo} alt="voxtox" />
					</ShareButton>
				</Show>
				<ShareButton onClick={handleShare}>
					<FaRegularClipboard font-size="60px" />
				</ShareButton>
				<ShareButton
					onClick={() =>
						window.open(`mailto:?subject=${props.tracklist?.name}&body=${getShareLink("E-Mail")}`, "_blank")
					}
				>
					<FaRegularEnvelope font-size="60px" />
				</ShareButton>
				<ShareButton
					onClick={() => window.open(`http://www.twitter.com/share?url=${getShareLink("Twitter")}`, "_blank")}
					bg="#04a9f4"
				>
					<FaBrandsTwitter font-size="60px" />
				</ShareButton>
				<ShareButton
					onClick={() =>
						window.open(`https://www.facebook.com/sharer/sharer.php?u=${getShareLink("Facebook")}`, "_blank")
					}
					bg="#1778f2"
				>
					<FaBrandsFacebookF font-size="60px" />
				</ShareButton>
				<ShareButton
					onClick={() =>
						window.open(`https://www.LinkedIn.com/shareArticle?mini=true&url=${getShareLink("LinkedIn")}`, "_blank")
					}
					bg="#0A66C2"
				>
					<FaBrandsLinkedin font-size="60px" />
				</ShareButton>

				<ShareButton onClick={handleShareCode}>
					<FaSolidCode font-size="60px" />
				</ShareButton>
			</ButtonsGrid>
		</Dialog>
	);
};
export default ShareDialog;
